'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTABanner from '@/components/CTABanner';
import FAQAccordion from '@/components/FAQAccordion';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const homeFAQs = [
{
  question: 'Can I get a business loan after bankruptcy?',
  answer:
  "A bankruptcy in your past doesn't necessarily preclude you from getting a small business loan, but it might make it more challenging. While not all lenders have the same requirements after bankruptcy, it's unlikely a borrower would qualify within the first year. Many lenders will require at least one year of improving credit history following the disposition of a bankruptcy."
},
{
  question: 'What is collateral?',
  answer:
  'Collateral is any asset or assets, which can be offered by a borrower to secure a loan. Should a borrower default, the lender can take possession of the asset, or assets, to satisfy the loan.'
},
{
  question: 'What is a personal guarantee on a small business loan?',
  answer:
  "It's common practice for lenders to require a personal guarantee from the business owner(s) to protect the lender should the business default on the loan. Lenders do this to mitigate the risk of lending to small businesses, and the guarantee is often a requirement by the lender before offering a loan."
},
{
  question: 'Do I need a business plan to get a loan?',
  answer:
  "That all depends upon the type of loan you're looking for. To qualify for an SBA loan you'll need a business plan. While other lenders might not require a formal business plan, they will ask questions about loan purpose, how this loan might positively impact profitability, etc."
}];


const loanCards = [
{
  iconBg: '#e8f7f3',
  iconSvg: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  title: 'Short Term Loan',
  body: 'A loan that is set to be paid back in a short period of time—typically within a year or two, sometimes longer. Most common uses include working capital, inventory or equipment purchases, and marketing. Short term loans also typically allow for quicker and easier access to funds with more flexible underwriting standards.'
},
{
  iconBg: '#e8f0f7',
  iconSvg: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5b7fa6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  title: 'Long Term Loan',
  body: 'A long-term loan is smart to consider when making a large investment or looking to expand, typically 5 to 10-year terms with potential up to 20+. These loans can have a fixed or floating interest rate. Longer term loans may also require collateral, such as real estate to be pledged when securing the loan.'
},
{
  iconBg: '#f7f0e8',
  iconSvg: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c47a2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  title: 'Line of Credit',
  body: 'A revolving loan that provides a fixed amount of capital and that can be accessed when needed. Unlike a traditional term loan, all or part of a line of credit can be accessed on demand up to a fixed limit. The customer pays interest only on the outstanding principal amount in use.'
},
{
  iconBg: '#f0f7e8',
  iconSvg: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5a8a2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  title: 'Merchant Cash Advance',
  body: "While not technically a loan, a cash advance typically does not require a true Personal Guarantee so may be riskier for the lender. Cash advances generally do not have a set term, payment schedule, or stated interest rate like a traditional loan. Instead, cash advances typically have a total payback and purchase a fixed percentage of the customer's future cash receivables."
}];


const mediaLogos = [
'Wall Street Journal',
'CNBC',
'Forbes',
'CNN',
'Fox Business',
'Entrepreneur'];


export default function HomePage() {
  useScrollReveal();

  return (
    <>
      <Navbar />
      <main>
        {/* HERO SECTION */}
        <section
          style={{
            background: '#ffffff',
            padding: '140px 60px 80px'
          }}>
          
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: '55% 45%',
              gap: '60px',
              alignItems: 'center'
            }}
            className="hero-grid">
            
            {/* Left Column */}
            <div className="reveal">
              <h1 style={{ marginBottom: '24px' }}>
                Funding to <em style={{ fontStyle: 'italic' }}>fuel</em> your business
              </h1>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1.05rem',
                  color: '#4a5568',
                  maxWidth: '480px',
                  lineHeight: 1.75,
                  marginBottom: '32px'
                }}>
                
                Compare multiple loan options at once and choose the best fit for your needs with confidence.
              </p>
              <Link href="/apply" className="btn-primary" style={{ display: 'inline-block', marginBottom: '16px' }}>
                See My Offers
              </Link>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  color: '#8a96a8',
                  marginTop: '12px'
                }}>
                
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8a96a8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:'5px'}}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>No impact to your credit score
              </p>
            </div>

            {/* Right Column */}
            <div
              className="reveal"
              style={{
                borderRadius: '20px',
                background: 'linear-gradient(145deg, #e8f7f3, #d0f0e8)',
                minHeight: '440px',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}>
              
              {/* Decorative circles */}
              <div
                style={{
                  position: 'absolute',
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  background: 'rgba(0,178,138,0.08)',
                  top: '-60px',
                  right: '-60px'
                }} />
              
              <div
                style={{
                  position: 'absolute',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: 'rgba(0,178,138,0.08)',
                  bottom: '-40px',
                  left: '-40px'
                }} />
              
              <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '440px' }}>
                <img
                  src="/assets/images/White_Modern_Support_Small_Business_Instagram_Post-1780344916998.png"
                  alt="Modern support for small business funding"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '440px',
                    objectFit: 'cover',
                    borderRadius: '20px',
                    display: 'block'
                  }} />
                
              </div>
            </div>
          </div>

          {/* As Seen In Strip */}
          <div
            style={{
              maxWidth: '1200px',
              margin: '60px auto 0',
              borderTop: '1px solid #e2e8f0',
              padding: '32px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px'
            }}
            className="seen-in-strip">
            
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: '#8a96a8'
              }}>
              
              As seen in
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '36px', alignItems: 'center' }}>
              {mediaLogos?.map((logo) =>
              <span
                key={logo}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#9aa5b4',
                  opacity: 0.5,
                  cursor: 'default',
                  transition: 'opacity 0.2s ease, color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.color = '#4a5568';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.5';
                  e.currentTarget.style.color = '#9aa5b4';
                }}>
                
                  {logo}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section
          style={{
            background: '#f7f9fc',
            padding: '100px 60px'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
            <p className="section-label reveal" style={{ marginBottom: '16px' }}>HOW IT WORKS</p>
            <h2 className="reveal" style={{ fontStyle: 'italic', marginBottom: '24px' }}>the process</h2>
            <p
              className="reveal"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                color: '#4a5568',
                lineHeight: 1.75,
                maxWidth: '640px',
                margin: '0 auto'
              }}>
              
              Answer some basic questions in less than 3 minutes with no impact to your credit score and compare multiple offers.{' '}
              <strong style={{ fontWeight: 700, color: '#1a2b4a' }}>YOU</strong> are in the driver&apos;s seat!
            </p>
            <div
              className="reveal"
              style={{
                maxWidth: '700px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                margin: '40px auto 0',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
              }}>
              
              <img
                src="/assets/images/lendo_app_mockup.png"
                alt="Lendo business loan application interface showing a multi-step form with loan type selection and business details"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }} />
              
            </div>
          </div>
        </section>

        {/* WHAT WE DO */}
        <section
          style={{
            background: '#ffffff',
            padding: '100px 60px'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <p className="section-label reveal" style={{ marginBottom: '16px' }}>SMALL BUSINESS ENTHUSIAST</p>
            <h2 className="reveal" style={{ marginBottom: '24px' }}>what we do</h2>
            <p
              className="reveal"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                color: '#4a5568',
                lineHeight: 1.75,
                maxWidth: '760px'
              }}>
              
              Using proprietary technology, we quickly review your business information and match you with the right financing options. We are connected to a vast network of lending professionals we handpicked to help fund your capital needs. With loan standards frequently changing, we stay on top of it all so we can deliver the right offers at the right time.
            </p>
            <p
              className="reveal"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                color: '#4a5568',
                lineHeight: 1.75,
                marginTop: '32px'
              }}>
              
              There are several different loan types available in the market. Here are a few you may encounter while considering options.
            </p>

            {/* 2x2 Card Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                marginTop: '40px'
              }}
              className="loan-cards-grid">
              
              {loanCards?.map((card, i) =>
              <div
                key={card?.title}
                className="card reveal"
                style={{
                  transitionDelay: `${i * 80}ms`
                }}>
                
                  <div
                  style={{
                    width: '48px',
                    height: '48px',
                    background: card?.iconBg,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    marginBottom: '16px'
                  }}>
                  
                    {card?.iconSvg}
                  </div>
                  <h3 style={{ marginBottom: '12px' }}>{card?.title}</h3>
                  <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.95rem',
                    color: '#4a5568',
                    lineHeight: 1.75,
                    margin: 0
                  }}>
                  
                    {card?.body}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section
          style={{
            background: '#ffffff',
            padding: '100px 60px'
          }}>
          
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <Link
              href="/faq"
              className="section-label reveal"
              style={{
                display: 'block',
                marginBottom: '16px',
                textDecoration: 'none'
              }}>
              
              FREQUENTLY ASKED QUESTIONS
            </Link>
            <FAQAccordion items={homeFAQs} />
          </div>
        </section>

        {/* CTA BANNER */}
        <CTABanner />
      </main>
      <Footer />
      <style>{`
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          .loan-cards-grid {
            grid-template-columns: 1fr !important;
          }
          section {
            padding: 60px 24px !important;
          }
          .seen-in-strip {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );

}